import { Alert, Button, CircularProgress, Collapse, Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

type ShippingTableView = "weight" | "product" | "weights";
type ShippingTableCountry = "us" | "ca" | "in";
type WeightTableDisplay = "ranges" | "orders_up_to";

interface ShippingTableTypeOption {
  label: string
  value: string
  zoneSourceValue?: string
}

type WindowWithLegacyShippingTables = Window & {
  AOW?: {
    ShippingTables?: new (slug: string) => unknown
  }
  $?: any
  jQuery?: any
}

const shippingMarkupCache = new Map<string, Promise<string>>();
const ALL_VIEWS: ShippingTableView[] = ["weight", "product", "weights"];
const VIEW_CONFIG: Record<ShippingTableView, { toggleId: string, sectionId: string, triggerSelector?: string }> = {
  weight: {
    toggleId: "rates-by-weight-toggle",
    sectionId: "rates-by-weight",
    triggerSelector: "#rates-by-weight-filter-country",
  },
  product: {
    toggleId: "rates-by-product-toggle",
    sectionId: "rates-by-product",
    triggerSelector: "#rates-by-product-filter-country",
  },
  weights: {
    toggleId: "product-weights-toggle",
    sectionId: "product-weights",
  },
};

interface Props {
  defaultView?: ShippingTableView
  allowedViews?: ShippingTableView[]
  typeOptionsByCountry?: Partial<Record<ShippingTableCountry, ShippingTableTypeOption[]>>
  weightTableDisplay?: WeightTableDisplay
}

function extractShippingTablesMarkup(html: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const shippingView = doc.querySelector("#shipping-view");
  const zoneValues = doc.querySelector("#rates-by-weight-filter-zone-values");

  if (!shippingView || !zoneValues) {
    throw new Error("Shipping tables markup not found");
  }

  return shippingView.outerHTML + `<div style="display:none">${zoneValues.outerHTML}</div>`;
}

function loadShippingTablesMarkup(url: string) {
  if (!shippingMarkupCache.has(url)) {
    shippingMarkupCache.set(url, fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load shipping tables: ${response.status}`);
        }

        return response.text();
      })
      .then(extractShippingTablesMarkup));
  }

  return shippingMarkupCache.get(url)!;
}

function formatPoundsFromOunces(ounces: number) {
  const pounds = ounces / 16;

  if (Number.isInteger(pounds)) {
    return `${pounds}`;
  }

  return pounds.toFixed(2).replace(/\.?0+$/, "");
}

function transformWeightTableToOrdersUpTo(table: HTMLTableElement, labels: { upTo: string, over: string }) {
  if (table.dataset.weightTableDisplay === "orders_up_to") return;

  const headerCell = table.querySelector("thead th");
  const bodyRows = Array.from(table.querySelectorAll("tbody tr"));

  if (!headerCell || bodyRows.length === 0) return;

  const upperBounds: number[] = [];

  bodyRows.forEach((row, index) => {
    const firstCell = row.querySelector("td");
    if (!firstCell) return;

    const match = firstCell.textContent?.match(/^\s*([\d.]+)\s*-\s*([\d.]+)\s*$/);
    if (!match) return;

    const upperBound = Number(match[2]);
    upperBounds.push(upperBound);

    if (index === bodyRows.length - 1) {
      const previousUpperBound = upperBounds[upperBounds.length - 2] ?? 0;
      firstCell.textContent = `${labels.over} ${formatPoundsFromOunces(previousUpperBound)} lb`;
      return;
    }

    firstCell.textContent = `${labels.upTo} ${formatPoundsFromOunces(upperBound)} lb`;
  });

  if (!upperBounds.length) return;

  headerCell.textContent = labels.upTo;
  table.dataset.weightTableDisplay = "orders_up_to";
}

export default function StoreShippingTablesEmbed(props: Props) {
  const [t] = useTranslation("account-v2");
  const [tR] = useTranslation("routes");

  const allowedViews = props.allowedViews && props.allowedViews.length > 0 ? props.allowedViews : ALL_VIEWS;
  const defaultView = allowedViews.includes(props.defaultView ?? "weight") ? (props.defaultView ?? "weight") : allowedViews[0];
  const typeOptionsByCountry = props.typeOptionsByCountry ?? {};
  const weightTableDisplay = props.weightTableDisplay ?? "ranges";

  const [opened, setOpened] = useState(false);
  const [html, setHtml] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!opened || html || hasError) return;

    let mounted = true;

    loadShippingTablesMarkup(tR("/info/production-and-shipping"))
      .then(markup => {
        if (!mounted) return;
        setHtml(markup);
      })
      .catch(() => {
        if (!mounted) return;
        setHasError(true);
      });

    return () => {
      mounted = false;
    };
  }, [hasError, html, opened, tR]);

  useEffect(() => {
    if (!html || initializedRef.current || !containerRef.current) return;

    const windowWithLegacyShippingTables = window as WindowWithLegacyShippingTables;
    const $ = windowWithLegacyShippingTables.$ ?? windowWithLegacyShippingTables.jQuery;

    if ($) {
      const container = $(containerRef.current);
      const allowedViewSet = new Set(allowedViews);

      container.find('input[name="shipping-view-sections"]').removeAttr("checked").prop("checked", false);

      ALL_VIEWS.forEach(view => {
        const config = VIEW_CONFIG[view];
        const toggle = container.find(`#${config.toggleId}`);
        const tab = container.find(`#shipping-view-tabs label[for="${config.toggleId}"]`).closest(".col");

        if (!allowedViewSet.has(view)) {
          toggle.hide();
          tab.hide();
        }
      });

      container.find(`#${VIEW_CONFIG[defaultView].toggleId}`).attr("checked", "checked").prop("checked", true);

      if (allowedViews.length <= 1) {
        container.find("#shipping-view-tabs").hide();
      }

      (Object.entries(typeOptionsByCountry) as [ShippingTableCountry, ShippingTableTypeOption[]][]).forEach(([country, options]) => {
        (["weight", "product"] as const).forEach(view => {
          if (!allowedViewSet.has(view)) return;

          const typeSelect = container.find(`#rates-by-${view}-filter-type-${country}`);
          if (!typeSelect.length) return;

          const zoneValuesSelect = container.find("#rates-by-weight-filter-zone-values");

          typeSelect.empty();

          options.forEach(option => {
            const replacementOption = $("<option />")
              .attr("value", option.value)
              .text(option.label);

            typeSelect.append(replacementOption);

            if (option.zoneSourceValue && zoneValuesSelect.length) {
              zoneValuesSelect.find(`option[data-id-shipping-slug="${option.value}"]`).remove();

              zoneValuesSelect
                .find(`option[data-id-shipping-slug="${option.zoneSourceValue}"]`)
                .clone()
                .attr("data-id-shipping-slug", option.value)
                .appendTo(zoneValuesSelect);
            }
          });

          typeSelect.val(options[0]?.value ?? "");
          typeSelect.closest(".input-field").toggle(options.length > 1);
        });
      });

      if (typeof container.find("select").material_select === "function") {
        container.find("select").material_select();
      }

      if (typeof container.find(".collapsible").collapsible === "function") {
        container.find(".collapsible").collapsible();
      }
    }

    if (windowWithLegacyShippingTables.AOW?.ShippingTables) {
      new windowWithLegacyShippingTables.AOW.ShippingTables("production-and-shipping");
    }

    if ($ && VIEW_CONFIG[defaultView].triggerSelector) {
      $(containerRef.current).find(VIEW_CONFIG[defaultView].triggerSelector!).trigger("change");
    }

    initializedRef.current = true;
  }, [allowedViews, defaultView, html, typeOptionsByCountry]);

  useEffect(() => {
    if (weightTableDisplay !== "orders_up_to" || !containerRef.current) return;

    const transformTables = () => {
      containerRef.current?.querySelectorAll<HTMLTableElement>('table[id^="rates-by-weight-"]').forEach(table => {
        transformWeightTableToOrdersUpTo(table, {
          upTo: t("Orders up to"),
          over: t("Orders over"),
        });
      });
    };

    transformTables();

    const observer = new MutationObserver(() => {
      transformTables();
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [html, t, weightTableDisplay]);

  return <Stack spacing={2} sx={{ width: "100%" }}>
    <Button
      variant="outlined"
      color="secondary"
      onClick={() => setOpened(value => !value)}
      endIcon={<ExpandMoreRoundedIcon
        fontSize="small"
        sx={{
          transform: opened ? "scaleY(-1)" : "scaleY(1)",
          transition: "transform 250ms"
        }}
      />}
    >{opened ? t("Hide shipping tables") : t("Show shipping tables")}</Button>

    <Collapse in={opened} timeout="auto" sx={{ width: "100%" }}>
      {hasError ? <Alert severity="error">
        {t("We couldn't load the shipping tables right now. Please try again later.")}
      </Alert> : null}

      {!hasError && !html ? <Stack direction="row" spacing={1.5} alignItems="center">
        <CircularProgress size={20} />
        <Typography variant="body2">{t("Loading shipping tables...")}</Typography>
      </Stack> : null}

      {html ? <div
        ref={containerRef}
        dangerouslySetInnerHTML={{ __html: html }}
      /> : null}
    </Collapse>
  </Stack>;
}
